import apiClient from './client'
import type { YtChannel } from './videoSettings.api'

export interface YtStreamSession {
    id: string
    tableId: number
    status: 'PREPARING' | 'LIVE' | 'STOPPED' | 'FAILED'
    ytBroadcastUrl: string | null
    tournamentName: string | null
    player1Name: string
    player2Name: string
    winsNeeded: number
    player1Wins: number
    player2Wins: number
    player1GameScore: number
    player2GameScore: number
    player1Handicap: string | null
    player2Handicap: string | null
    isBreak: boolean
    breakStartedAt: string | null
    startedAt: string
    stage: string | null
    isPublic: boolean
    ytChannelId: number | null
    volumeDb: number
    commentaryVolumeDb: number
    commentaryEnabled: boolean
    deliveryMode: 'FFMPEG' | 'OBS'
    /** Only present for OBS sessions (staff view). */
    overlayToken: string | null
    shotClockActivePlayer: 1 | 2 | null
    player1ExtAvailable: boolean
    player2ExtAvailable: boolean
    matchDurationMinutes: number | null
    matchRemainingMs: number | null
    matchRunning: boolean
}

export interface StartStreamRequest {
    player1Name: string
    player2Name: string
    player1Handicap?: string
    player2Handicap?: string
    winsNeeded: number
    tournamentName?: string
    stage?: string
    descriptionPrefix?: string
    channelId?: number
    /** OBS sessions only: total match time in minutes (omit = no match clock). */
    matchDurationMinutes?: number
}

export interface UpdateSessionSettingsRequest {
    player1Name?: string
    player2Name?: string
    player1Handicap?: string
    player2Handicap?: string
    stage?: string
    tournamentName?: string
}

export interface ScoreUpdateRequest {
    player1Wins: number
    player2Wins: number
    player1GameScore: number
    player2GameScore: number
}

export type ShotClockCommand = 'START' | 'PAUSE' | 'RESUME' | 'RESET' | 'SWITCH' | 'EXTEND' | 'EXPIRED' | 'GAME_RESET'

export interface ShotClockUpdateRequest {
    action: ShotClockCommand
    activePlayer: 1 | 2
    /** Time on the clock right after the command (ms). */
    remainingMs: number
    player1ExtAvailable: boolean
    player2ExtAvailable: boolean
}

export interface YtSponsor {
    id: number
    name: string
    type: 'CLUB' | 'MAJOR' | 'MINOR'
    filePath: string
    posX: number
    posY: number
    width: number
    height: number
    displayOrder: number
    active: boolean
}

export interface YtSponsorRequest {
    name: string
    type: 'CLUB' | 'MAJOR' | 'MINOR'
    filePath: string
    posX: number
    posY: number
    width: number
    height: number
    displayOrder: number
    active: boolean
}

export interface YtStreamTableSettings {
    tableId: number
    tableName: string
    streamKey: string | null
    bitrateKbps: number
}

export const startStream = async (tableId: number, req: StartStreamRequest): Promise<YtStreamSession> => {
    const res = await apiClient.post(`/streaming/tables/${tableId}/start`, req)
    return res.data.data as YtStreamSession
}

export const stopStream = async (tableId: number): Promise<void> => {
    await apiClient.post(`/streaming/tables/${tableId}/stop`)
}

export const updateScore = async (tableId: number, req: ScoreUpdateRequest): Promise<YtStreamSession> => {
    const res = await apiClient.put(`/streaming/tables/${tableId}/score`, req)
    return res.data.data as YtStreamSession
}

export const sendShotClock = async (tableId: number, req: ShotClockUpdateRequest): Promise<YtStreamSession> => {
    const res = await apiClient.put(`/streaming/tables/${tableId}/shot-clock`, req)
    return res.data.data as YtStreamSession
}

export const startObsSession = async (tableId: number, req: StartStreamRequest): Promise<YtStreamSession> => {
    const res = await apiClient.post(`/streaming/tables/${tableId}/obs-session`, req)
    return res.data.data as YtStreamSession
}

export const updateSessionSettings = async (
    tableId: number,
    req: UpdateSessionSettingsRequest,
): Promise<YtStreamSession> => {
    const res = await apiClient.put(`/streaming/tables/${tableId}/settings`, req)
    return res.data.data as YtStreamSession
}

export const getStreamingChannels = async (): Promise<YtChannel[]> => {
    const res = await apiClient.get('/streaming/channels')
    return res.data.data as YtChannel[]
}

export const startBreak = async (tableId: number): Promise<YtStreamSession> => {
    const res = await apiClient.post(`/streaming/tables/${tableId}/break/start`)
    return res.data.data as YtStreamSession
}

export const stopBreak = async (tableId: number): Promise<YtStreamSession> => {
    const res = await apiClient.post(`/streaming/tables/${tableId}/break/stop`)
    return res.data.data as YtStreamSession
}

export const getActiveSession = async (tableId: number): Promise<YtStreamSession | null> => {
    try {
        const res = await apiClient.get(`/streaming/tables/${tableId}/session`)
        return res.data.data as YtStreamSession
    } catch (err: any) {
        if (err?.response?.status === 404) return null
        throw err
    }
}

export const getSponsors = async (): Promise<YtSponsor[]> => {
    const res = await apiClient.get('/settings/sponsors')
    return res.data.data as YtSponsor[]
}

export const createSponsor = async (req: YtSponsorRequest): Promise<YtSponsor> => {
    const res = await apiClient.post('/settings/sponsors', req)
    return res.data.data as YtSponsor
}

export const updateSponsor = async (id: number, req: YtSponsorRequest): Promise<YtSponsor> => {
    const res = await apiClient.put(`/settings/sponsors/${id}`, req)
    return res.data.data as YtSponsor
}

export const deleteSponsor = async (id: number): Promise<void> => {
    await apiClient.delete(`/settings/sponsors/${id}`)
}

export const makePublic = async (tableId: number): Promise<YtStreamSession> => {
    const res = await apiClient.post(`/streaming/tables/${tableId}/make-public`)
    return res.data.data as YtStreamSession
}

export const updateVolume = async (tableId: number, volumeDb: number): Promise<void> => {
    await apiClient.put(`/streaming/tables/${tableId}/volume`, { volumeDb }, { _silent: true } as any)
}

export const updateCommentary = async (
    tableId: number,
    volumeDb: number,
    enabled: boolean,
): Promise<YtStreamSession> => {
    const res = await apiClient.put(
        `/streaming/tables/${tableId}/commentary`,
        { volumeDb, enabled },
        { _silent: true } as any,
    )
    return res.data.data as YtStreamSession
}

export const getStreamTableSettings = async (): Promise<YtStreamTableSettings[]> => {
    const res = await apiClient.get('/settings/youtube-stream-tables')
    return res.data.data as YtStreamTableSettings[]
}

export const fetchSponsorPreview = async (filePath: string): Promise<Blob> => {
    const res = await apiClient.get('/settings/sponsors/preview', {
        params: { filePath },
        responseType: 'blob',
    })
    return res.data as Blob
}

export const updateStreamTableSettings = async (
    tableId: number,
    streamKey: string,
    bitrateKbps: number,
): Promise<YtStreamTableSettings> => {
    const res = await apiClient.put(`/settings/youtube-stream-tables/${tableId}`, {
        streamKey,
        bitrateKbps,
    })
    return res.data.data as YtStreamTableSettings
}

export const resetYtStream = async (tableId: number): Promise<void> => {
    await apiClient.delete(`/settings/youtube-stream-tables/${tableId}/yt-stream`)
}

export const exportSponsors = async (): Promise<YtSponsor[]> => {
    const res = await apiClient.get('/settings/sponsors/export')
    return res.data as YtSponsor[]
}

export const importSponsors = async (sponsors: YtSponsorRequest[]): Promise<YtSponsor[]> => {
    const res = await apiClient.post('/settings/sponsors/import', sponsors)
    return res.data.data as YtSponsor[]
}
